import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import perplexityService from '../../../services/perplexityService';
import toast from '../../../components/ui/Toast';

const CompetitorComparison = ({ userId }) => {
  const [loading, setLoading] = useState(false);
  const [competitors, setCompetitors] = useState([
    { name: 'Your Company', marketShare: 15, growth: 12.5, rating: 4.5, pricing: '$99/mo' },
    { name: 'Competitor A', marketShare: 28, growth: 8.3, rating: 4.2, pricing: '$149/mo' },
    { name: 'Competitor B', marketShare: 22, growth: 15.7, rating: 4.6, pricing: '$129/mo' },
    { name: 'Competitor C', marketShare: 18, growth: 6.1, rating: 3.9, pricing: '$89/mo' },
    { name: 'Competitor D', marketShare: 17, growth: 10.2, rating: 4.3, pricing: '$119/mo' }
  ]);
  const [researchQuery, setResearchQuery] = useState('');
  const [researchResults, setResearchResults] = useState(null);

  const metrics = [
    { key: 'marketShare', label: 'Market Share', suffix: '%', color: 'text-primary' },
    { key: 'growth', label: 'Growth Rate', suffix: '%', color: 'text-success' },
    { key: 'rating', label: 'User Rating', suffix: '/5', color: 'text-warning' },
    { key: 'pricing', label: 'Pricing', suffix: '', color: 'text-foreground' }
  ];

  const handleResearch = async () => {
    if (!researchQuery?.trim()) {
      toast?.warning('Please enter a research query');
      return;
    }

    setLoading(true);
    try {
      const result = await perplexityService?.searchWeb(
        `Competitive analysis: ${researchQuery}. Provide market positioning, strengths, weaknesses, and key differentiators.`,
        { searchDepth: 'high', maxTokens: 2048 }
      );
      
      setResearchResults(result);
      toast?.success('Competitor research completed');
    } catch (error) {
      console.error('Research error:', error);
      toast?.error('Failed to fetch competitor insights');
    } finally {
      setLoading(false);
    }
  };

  const getMaxValue = (key) => {
    return Math.max(...competitors?.map(c => parseFloat(c?.[key]) || 0));
  };

  const getBarWidth = (value, key) => {
    const max = getMaxValue(key);
    return (parseFloat(value) / max) * 100;
  };

  return (
    <div className="space-y-6">
      {/* Research Input */}
      <div className="bg-background border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="Search" size={24} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">Competitor Research</h3>
        </div>
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Enter competitor name or market segment..."
            value={researchQuery}
            onChange={(e) => setResearchQuery(e?.target?.value)}
            className="flex-1 px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            onKeyDown={(e) => e?.key === 'Enter' && handleResearch()}
          />
          <Button
            onClick={handleResearch}
            disabled={loading}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {loading ? (
              <Icon name="Loader2" size={18} className="animate-spin" />
            ) : (
              <Icon name="Search" size={18} />
            )}
          </Button>
        </div>
      </div>

      {/* Research Results */}
      {researchResults && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="Lightbulb" size={24} color="var(--color-primary)" />
            <h3 className="text-lg font-semibold text-foreground">AI-Powered Insights</h3>
          </div>
          <div className="prose prose-sm max-w-none text-foreground">
            <p className="whitespace-pre-wrap">{researchResults?.content}</p>
          </div>
          {researchResults?.sources?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-sm font-medium text-muted-foreground mb-2">Sources:</p>
              <div className="space-y-1">
                {researchResults?.sources?.map((source, index) => (
                  <a
                    key={index}
                    href={source}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline block"
                  >
                    {source}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Comparison Table */}
      <div className="bg-background border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="BarChart3" size={24} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">Performance Comparison</h3>
        </div>
        <div className="space-y-8">
          {metrics?.map((metric) => (
            <div key={metric?.key}>
              <h4 className="text-sm font-semibold text-muted-foreground mb-3">{metric?.label}</h4>
              <div className="space-y-2">
                {competitors?.map((competitor, index) => {
                  const value = competitor?.[metric?.key];
                  const isYourCompany = index === 0;
                  const barWidth = metric?.key !== 'pricing' ? getBarWidth(value, metric?.key) : 100;
                  
                  return (
                    <div key={competitor?.name} className="flex items-center gap-3">
                      <div className="w-32 text-sm font-medium text-foreground truncate">
                        {competitor?.name}
                        {isYourCompany && <span className="text-primary ml-1">(You)</span>}
                      </div>
                      <div className="flex-1">
                        {metric?.key !== 'pricing' ? (
                          <div className="relative">
                            <div className="w-full bg-muted rounded-full h-8 overflow-hidden">
                              <div
                                className={`h-full flex items-center justify-end pr-3 text-white text-xs font-semibold transition-all duration-500 ${
                                  isYourCompany ? 'bg-primary' : 'bg-muted-foreground'
                                }`}
                                style={{ width: `${barWidth}%` }}
                              >
                                {value}{metric?.suffix}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm font-medium text-foreground">{value}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Market Position */}
      <div className="bg-background border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="Target" size={24} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">Market Positioning</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-success/5 border border-success/20 rounded-lg p-4">
            <h4 className="font-semibold text-success mb-2 flex items-center gap-2">
              <Icon name="TrendingUp" size={18} />
              Competitive Advantages
            </h4>
            <ul className="space-y-2 text-sm text-foreground">
              <li className="flex items-start gap-2">
                <Icon name="Check" size={16} className="text-success mt-0.5" />
                <span>Strong growth rate (12.5% vs industry avg 10.5%)</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="Check" size={16} className="text-success mt-0.5" />
                <span>Competitive pricing with high value proposition</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="Check" size={16} className="text-success mt-0.5" />
                <span>Above-average user satisfaction rating</span>
              </li>
            </ul>
          </div>
          <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
            <h4 className="font-semibold text-warning mb-2 flex items-center gap-2">
              <Icon name="AlertTriangle" size={18} />
              Areas for Improvement
            </h4>
            <ul className="space-y-2 text-sm text-foreground">
              <li className="flex items-start gap-2">
                <Icon name="AlertCircle" size={16} className="text-warning mt-0.5" />
                <span>Market share below top 2 competitors</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="AlertCircle" size={16} className="text-warning mt-0.5" />
                <span>Need to increase brand awareness</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="AlertCircle" size={16} className="text-warning mt-0.5" />
                <span>Opportunity to expand feature set</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitorComparison;